import { useMemo } from 'react';
import { useData } from '../contexts/DataContext';
import { useSelection } from '../contexts/SelectionContext';

/**
 * Get percentage data for visualization based on origin or destination selection
 * @param {object} percentageData - Full percentage data object
 * @param {string} dayType - Day type (e.g., "Feriali")
 * @param {string} timeSlot - Time slot (e.g., "08:00 - 09:00")
 * @param {string} origin - Selected origin zone
 * @param {string} destination - Selected destination zone
 * @returns {object} Map of zone -> { trips, originPercentage, destinationPercentage }
 */
const getPercentageVisualizationData = (percentageData, dayType, timeSlot, origin, destination) => {
  const zonePercentageMap = new Map();
  let totalTrips = 0;
  let maxPercentage = 0;

  if (!percentageData || !dayType || !timeSlot || (!origin && !destination)) {
    return { zonePercentageMap, totalTrips, maxPercentage };
  }

  const dayData = percentageData[dayType];
  if (!dayData) {
    return { zonePercentageMap, totalTrips, maxPercentage };
  }

  const timeData = dayData[timeSlot];
  if (!timeData) {
    return { zonePercentageMap, totalTrips, maxPercentage };
  }

  // Handle origin-based filtering (show destinations)
  if (origin) {
    const originData = timeData.filter((item) => item.origin === origin);

    originData.forEach((item) => {
      zonePercentageMap.set(item.destination, {
        trips: item.trips,
        originPercentage: item.origin_percentage,
        destinationPercentage: item.destination_percentage,
      });
      totalTrips += item.trips;
      if (item.origin_percentage > maxPercentage) {
        maxPercentage = item.origin_percentage;
      }
    });
  }
  // Handle destination-based filtering (show origins)
  else if (destination) {
    const destinationData = timeData.filter((item) => item.destination === destination);

    destinationData.forEach((item) => {
      zonePercentageMap.set(item.origin, {
        trips: item.trips,
        originPercentage: item.destination_percentage, // Use destination_percentage when showing origins
        destinationPercentage: item.origin_percentage,
      });
      totalTrips += item.trips;
      if (item.destination_percentage > maxPercentage) {
        maxPercentage = item.destination_percentage;
      }
    });
  }

  return { zonePercentageMap, totalTrips, maxPercentage };
};

/**
 * Hook for filtering and processing trips data based on current selection
 * @param {number} viewId - View ID (1 or 2) for compare mode
 * @returns {object} Processed trips data for visualization
 */
export const useTripsData = (viewId = 1) => {
  const { percentageData } = useData();
  const { getViewSelection } = useSelection();

  const { origin, destination, dayType, timeSlot } = getViewSelection(viewId);

  // Get visualization data based on percentage data and origin/destination selection
  const visualizationData = useMemo(() => {
    return getPercentageVisualizationData(percentageData, dayType, timeSlot, origin, destination);
  }, [percentageData, dayType, timeSlot, origin, destination]);

  // Check if we have valid selections
  const hasSelection = origin || destination;
  const hasValidTimeSelection = dayType && timeSlot;

  return {
    zonePercentageMap: visualizationData.zonePercentageMap,
    totalTrips: visualizationData.totalTrips,
    maxPercentage: visualizationData.maxPercentage,
    hasSelection,
    hasValidTimeSelection,
    origin,
    destination,
    dayType,
    timeSlot,
  };
};

export default useTripsData;
